import { useEffect, useState } from "react";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import { fetchRecordInCRM } from "../../../apis/ext";
import { useMutation } from "react-query";
import { isEqual } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createCRMRecords } from "../../../apis/crm";
import { ChevronDown, ChevronUp, PencilIcon } from "lucide-react";
import { areAllKeysEmpty, normalizeInitialValues, truncateValue } from "../../../utils/GenericFn";
import { Skeleton } from "../../ui/skeleton";

export default function CreateCompanyRecord({
  apiLabel,
  apiName,
  checkedApiName,
  type = "company",
  infoData,
  goBack,
  onSuccess,
  generatePayload = false,
  getGeneratedPayload,
  initialPayloadforForm,
}) {
  const captureEvent = useCaptureEvent();
  const [errorForCreate, setErrorForCreate] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [fetchedRecordObj, setFetchedRecordObj] = useState({});

  const fetchRecordInCRMFn = useMutation("fetch_record_in_crm", fetchRecordInCRM, {
    onSuccess(resdata, variables, context) {
      let data = resdata && resdata.length > 0 && resdata[0];
      if (data && data.data) {
        const obj = data.data;
        console.log("obj setFetchedRecordObj", obj);
        setFetchedRecordObj(obj);
        setTimeout(() => {
          if (!areAllKeysEmpty(initialPayloadforForm)) {
            Object.entries(initialPayloadforForm).forEach(([key, value]) => {
              formik.setFieldValue(key, value);
            });
          }
        }, 200);
      }
    },
    onError(error) {
      console.log(error);
    },
  });
  const handleFetchRecordInCRM = (apiName = apiName, searchParams = {}, search = false) => {
    console.log("infoData", infoData);
    let payload = {
      organization_id: infoData?.record_id,
      crm_object_api_name: apiName,
      type: type,
    };

    payload.on_create = true;

    fetchRecordInCRMFn.mutate({
      data: [{ ...payload }],
      configId: infoData?.activeCrm,
      options: {
        headers: { "X-Workspace-Id": infoData?.activeWorkspaceId },
      },
    });
  };

  const getChangedValues = (values, initialValues) => {
    const changedValues = {};
    Object.keys(values).forEach((key) => {
      if (!isEqual(values[key], initialValues[key])) {
        changedValues[key] = values[key];
      }
    });
    return changedValues;
  };

  const cleanPayload = (payload) => {
    // Helper function to remove undefined keys
    const cleanObject = (obj = {}) => {
      return Object.keys(obj).reduce((acc, key) => {
        if (obj[key] !== undefined) {
          acc[key] = obj[key];
        }
        return acc;
      }, {});
    };

    // Clean the input_fields and reference_fields objects
    payload.input_fields = cleanObject(payload.input_fields);
    payload.reference_fields = cleanObject(payload.reference_fields);

    // Remove input_fields object if it's empty
    if (Object.keys(payload.input_fields).length === 0) {
      delete payload.input_fields;
    }

    // Remove reference_fields object if it's empty
    if (Object.keys(payload.reference_fields).length === 0) {
      delete payload.reference_fields;
    }

    return payload;
  };

  const getValidationSchema = (fetchedRecordObj) => {
    const shape = {};
    Object.keys(fetchedRecordObj).forEach((key) => {
      const fieldData = fetchedRecordObj[key];
      const inputName = key || "";
      if (fieldData?.required) {
        shape[inputName] = Yup.string().required(`${fieldData?.label} is required`).trim();
      } else {
        shape[inputName] = Yup.string().nullable();
      }
    });
    return Yup.object().shape(shape);
  };

  const createRecordFn = useMutation("create_record", createCRMRecords, {
    onSuccess(data, variables, context) {
      onSuccess(data);
      console.log("ECRM_RecordCreated", {
        type: type,
        crm_object_name: apiLabel,
      });
      captureEvent("ECRM_RecordCreated", {
        type: type,
        crm_object_name: apiLabel,
      });
    },
    onError(error) {
      console.log(error);
    },
  });

  const formik = useFormik({
    initialValues: normalizeInitialValues(initialValues),
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: getValidationSchema(fetchedRecordObj),
    onSubmit: (inputvalues) => {
      const values = getChangedValues(inputvalues, formik.initialValues);
      console.log("formik.initialValues", formik.initialValues);
      const input_fields = {};

      Object.keys(fetchedRecordObj).forEach((key) => {
        const fieldData = fetchedRecordObj[key];
        const inputName = key || "";
        input_fields[inputName] = values[inputName];
      });

      const initPayload = {
        organization_id: infoData?.record_id,
        crm_object_api_name: apiName,
        type: type,
        input_fields: input_fields,
      };

      const payload = cleanPayload(initPayload);
      console.log("final payload of create company", payload);
      if (getGeneratedPayload && generatePayload) {
        getGeneratedPayload(payload?.input_fields);
        captureEvent("ECRM_CreateNewForm_PayloadGenerated", {
          type: type,
          crm_object_name: apiLabel,
        });
      }
      if (!generatePayload) {
        createRecordFn.mutate({
          data: { requests: [{ ...payload }] },
          configId: infoData?.activeCrm,
          options: {
            headers: {
              "X-Workspace-Id": infoData?.activeWorkspaceId,
              "Record-Source": process.env.REACT_APP_APP_NAME,
            },
          },
        });
      }
    },
  });

  useEffect(() => {
    if (fetchedRecordObj && Object.keys(fetchedRecordObj).length > 0) {
      const updatedInitialValues = Object.keys(fetchedRecordObj).reduce((acc, key) => {
        const fieldData = fetchedRecordObj[key]; // Get the inner object
        const fieldName = key || ""; // Ensure inner object exists
        acc[fieldName] = fieldData?.value || ""; // Get the value, fallback to an empty string
        return acc;
      }, {});

      setInitialValues(updatedInitialValues); // Set initial values once object is populated
    }
  }, [fetchedRecordObj]); // Re-run the effect when fetchedRecordObj changes

  useEffect(() => {
    if (apiName) {
      handleFetchRecordInCRM(apiName);
    }
  }, [apiName]);

  const INITIAL_DISPLAY_LIMIT = 5;

  const [showAllNonCustomFields, setShowAllNonCustomFields] = useState(false);
  const [showAllCustomFields, setShowAllCustomFields] = useState(false);

  // Get lists of non-custom and custom fields
  const nonCustomFields = Object.keys(fetchedRecordObj).filter(
    (label) => !fetchedRecordObj[label]?.is_custom
  );
  const customFields = Object.keys(fetchedRecordObj).filter((label) => fetchedRecordObj[label]?.is_custom);

  const countNonCustomFieldsWithValue = () => {
    const data = formik.values;
    return Object.entries(data).every(([key, value]) => {
      // const referenceField = Object.values(fetchedRecordObj).find((ref) => ref[key] !== undefined);
      const referenceField = fetchedRecordObj[key];
      if (!referenceField) {
        return true;
      }

      const isCustomKey = referenceField.is_custom;

      if (!isCustomKey) {
        return value && typeof value === "string" && value.trim() !== "";
      }
      return true;
    });
  };

  // Function to render each field
  const renderField = (label, inputName, fieldData, custom = false) => {
    return (
      <div
        key={inputName}
        className={`mb-3 ${errorForCreate && errorForCreate?.sytem_field_key == inputName && "error__field"}`}
      >
        {editingField === inputName || (!formik.values[inputName] && fieldData?.required) ? (
          <>
            <p className="mb-2 text-[#344054] text-sm field__label">
              {label} {fieldData?.required && <span className="text-red-500">*</span>}
            </p>
            <div
              className="flex justify-between"
              onClick={() => {
                if (!formik.values[inputName] && fieldData?.required) {
                  setEditingField(inputName);
                }
              }}
            >
              {fieldData.picklist_values && fieldData.picklist_values.length > 0 ? (
                <select
                  name={inputName}
                  className="rounded-[8px] w-full bg-white border border-[#dddddd] text-[#11181C] text-[14px] p-2 h-10"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values[inputName] || ""}
                >
                  <option value="">--Select--</option>
                  {fieldData.picklist_values.map((el) => (
                    <option key={el} value={el}>
                      {el}
                    </option>
                  ))}
                </select>
              ) : (
                <textarea
                  name={inputName}
                  className={`rounded-[8px] w-full bg-white border border-[#dddddd] text-[#11181C] text-[14px] p-2 h-10 overflow-x-hidden ${
                    formik.values[inputName] && formik.values[inputName].length > 60
                      ? "h-[99px]"
                      : "whitespace-pre resize-none"
                  }`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values[inputName] || ""}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Return") {
                      e.preventDefault();
                      setEditingField(null);
                      setErrorForCreate({});
                    }
                  }}
                />
              )}
              {!formik.errors[inputName] && (
                <span
                  className="rounded-[8px] bg-white border border-[#dddddd] text-[#11181C] text-[14px] p-2 px-3 h-10 inline-flex items-center justify-center ml-2 cursor-pointer"
                  onClick={() => {
                    setEditingField(null);
                    setErrorForCreate({});
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.6668 5L7.50016 14.1667L3.3335 10"
                      stroke="#6941C6"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </div>
            {formik.touched[inputName] && formik.errors[inputName] && (
              <p className="text-red-700 text-xs mt-2">{formik.errors[inputName]}</p>
            )}
          </>
        ) : (
          <>
            <p
              className={`mb-2 text-[#344054] text-sm group ${
                !formik.values[inputName] &&
                !fieldData?.required &&
                !showAllNonCustomFields &&
                !custom &&
                "hidden"
              }`}
              onClick={() => {
                setEditingField(inputName);
                captureEvent("ECRM_CreateNewForm_EditField", {
                  type: type,
                  field_name: inputName,
                });
              }}
            >
              <span className="w-full flex space-x-2 items-center">
                <span className="text-xs field__label">
                  {" "}
                  {label} {fieldData?.required && <span className="text-red-500">*</span>}
                </span>
                <PencilIcon width={14} height={14} className="ml-2 hidden group-hover:block cursor-pointer" />
              </span>
              <b style={{ wordBreak: "break-all" }} className="block">
                {formik.values[inputName] ? truncateValue(formik.values[inputName]) : "-"}
              </b>
            </p>
          </>
        )}
        {errorForCreate && errorForCreate?.sytem_field_key == inputName && (
          <p className="text-red-700 text-xs mt-2">{errorForCreate?.message}</p>
        )}
      </div>
    );
  };

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit(e);
        }}
        className="mt-4 animate__animated animate__fadeIn pb-6"
      >
        {fetchRecordInCRMFn.isLoading ? (
          <>
            <div className="mb-3">
              <Skeleton className="h-[20px] w-[100px] mb-2" />
              <Skeleton className="h-[40px] w-full" />
            </div>
            <div className="mb-3">
              <Skeleton className="h-[20px] w-[100px] mb-2" />
              <Skeleton className="h-[40px] w-full" />
            </div>
            <div className="mb-3">
              <Skeleton className="h-[20px] w-[100px] mb-2" />
              <Skeleton className="h-[40px] w-full" />
            </div>
            <div className="mb-3">
              <Skeleton className="h-[20px] w-[100px] mb-2" />
              <Skeleton className="h-[40px] w-full" />
            </div>
          </>
        ) : (
          <>
            <>
              {/* Non-Custom Fields Section */}
              {nonCustomFields.length > 0 && (
                <div className="non-custom-field-container border p-4 rounded-lg mb-4 pb-1 bg-white">
                  {nonCustomFields.map((key, index) => {
                    const fieldData = fetchedRecordObj[key];
                    const inputName = key || "";

                    // Conditionally render based on display limit
                    const isVisible = showAllNonCustomFields || index < INITIAL_DISPLAY_LIMIT;

                    return (
                      <div
                        key={key}
                        style={{
                          display: isVisible
                            ? "block"
                            : formik.values[inputName] || fieldData?.required
                              ? ""
                              : "none",
                        }}
                      >
                        {renderField(fieldData?.label, inputName, fieldData)}
                      </div>
                    );
                  })}
                  {nonCustomFields.length > INITIAL_DISPLAY_LIMIT && !countNonCustomFieldsWithValue() && (
                    <span
                      className="text-[#6941C6] text-sm my-2 flex items-center font-bold text-center space-x-2 justify-center cursor-pointer"
                      onClick={() => {
                        setShowAllNonCustomFields(!showAllNonCustomFields);
                        captureEvent("ECRM_CreateNewForm_ShowAllBtn", {
                          type: type,
                          custom: false,
                        });
                      }}
                    >
                      <span>{showAllNonCustomFields ? "Show less" : "Show all"}</span>
                      {showAllNonCustomFields ? (
                        <ChevronUp width={14} height={14} />
                      ) : (
                        <ChevronDown width={14} height={14} />
                      )}
                    </span>
                  )}
                </div>
              )}

              {customFields.length > 0 && (
                <div className="custom-field-container border p-4 rounded-lg mb-4 pb-1 bg-white">
                  {customFields.map((key, index) => {
                    const fieldData = fetchedRecordObj[key];
                    const inputName = key || "";

                    // Conditionally render based on display limit
                    const isVisible = showAllCustomFields || index < INITIAL_DISPLAY_LIMIT;

                    return (
                      <div
                        key={key}
                        style={{
                          display: isVisible || fieldData?.required ? "block" : "none",
                        }}
                      >
                        {renderField(fieldData?.label, inputName, fieldData, true)}
                      </div>
                    );
                  })}
                  {customFields.length > INITIAL_DISPLAY_LIMIT && (
                    <span
                      className="text-[#6941C6] text-sm my-2 flex items-center font-bold text-center space-x-2 justify-center cursor-pointer"
                      onClick={() => {
                        setShowAllCustomFields(!showAllCustomFields);
                        captureEvent("ECRM_CreateNewForm_ShowAllBtn", {
                          type: type,
                          custom: true,
                        });
                      }}
                    >
                      <span>{showAllCustomFields ? "Show less" : "Show all"}</span>
                      {showAllCustomFields ? (
                        <ChevronUp width={14} height={14} />
                      ) : (
                        <ChevronDown width={14} height={14} />
                      )}
                    </span>
                  )}
                </div>
              )}
            </>
          </>
        )}
        <div className="flex items-center mt-4 space-x-2">
          <button
            disabled={createRecordFn.isLoading || fetchRecordInCRMFn.isLoading || !formik.isValid}
            className="text-[14px] flex-1 disabled:opacity-70 flex w-8/12 justify-center items-center py-2 h-9 space-x-2 px-1 rounded-[8px] text-white bg-[#7F56D9] dark:bg-brand-300 border border-[#7F56D9] "
            onClick={(e) => {
              e.preventDefault();
              formik.handleSubmit(e);
              captureEvent("ECRM_CreateNewForm_Submit", {
                type: type,
                crm_object_name: apiLabel,
              });
            }}
          >
            {generatePayload ? "Confirm" : "Create record"}
            {createRecordFn.isLoading && <div className="loader_circle ml-2 inline-block"></div>}
          </button>

          <button
            onClick={(e) => {
              e.preventDefault();

              goBack();
            }}
            className="inline-flex  w-4/12 justify-center items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50  bg-white border text-[#475467] px-4 py-2 h-9 rounded-[8px]"
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
}
